/**
 * Email format + compose helpers for grid email cells.
 *
 * The compose action is funnelled through `buildMailtoHref` / `openEmailCompose` deliberately: today
 * it hands off to the OS mail client via `mailto:`, which needs no infrastructure but records nothing
 * in FBO. If compose ever moves in-app, this file is the only place that changes.
 */

/**
 * Same pattern `client-request-modal.tsx` already validates hiringManagerEmail with, so the grid and
 * the modal agree on what counts as an email. Deliberately permissive — it checks shape
 * (`local@domain.tld`, no whitespace) rather than attempting RFC 5322, which no practical regex
 * matches and which would reject addresses that deliver fine.
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True when `value` looks like a single email address. Empty/absent is NOT a format error. */
export function isValidEmailFormat(value: unknown): boolean {
  if (value === null || value === undefined) return true
  const text = String(value).trim()
  if (text === '') return true
  return EMAIL_PATTERN.test(text)
}

/**
 * `mailto:` href for a single recipient, or null when the value isn't a usable address — callers use
 * null to decide whether to render the action at all, so a malformed cell (which is possible: no
 * email field ships a validation regex, so historical rows can hold anything) offers no dead link.
 *
 * encodeURIComponent, not raw interpolation: an address containing `?` or `&` would otherwise be
 * parsed as the start of mailto header fields, letting stored data inject `subject`/`cc`/`bcc`.
 */
export function buildMailtoHref(value: unknown): string | null {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  if (text === '' || !EMAIL_PATTERN.test(text)) return null
  return `mailto:${encodeURIComponent(text)}`
}

/**
 * Opens the compose window for `value`. No-op when the address is unusable.
 *
 * Assigns to `window.location.href` rather than `window.open`: a `mailto:` handoff doesn't open a
 * browsing context, so `window.open` leaves a blank tab behind in some browsers.
 */
export function openEmailCompose(value: unknown): void {
  const href = buildMailtoHref(value)
  if (!href || typeof window === 'undefined') return
  window.location.href = href
}
